import { Component, HostListener, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserReadableError } from '../../../../app/api/errors/user-readable.error';
import { Observable, Subscription } from 'rxjs';
import { CustomerVerificationRequestsService } from '../../../../app/modules/customer-tasks/customer-verification-requests/services/customer-verification-requests.service';

export type ConfirmDialogButtonType = 'primary' | 'secondary' | 'negative';

export interface ConfirmDialogButton {
    text: string;
    type?: ConfirmDialogButtonType;
    action?: () => Observable<any> | void;
    actionWithId?: (id: string) => Observable<any> | void;
}

export interface ConfirmDialogData {
    title: string;
    description: string;
    optional_description?: string;
    hasNote?: boolean;
    primaryButton: ConfirmDialogButton;
    secondaryButton?: ConfirmDialogButton;
    cancelButton?: ConfirmDialogButton;
    stackButtonsVertically?: boolean;
    disableDialogClose?: boolean;
    className?: string;
    second_description?: string;
}

export interface ConfirmDialogResult {
    chosePrimary: boolean;
    choseSecondary: boolean;
    choseCancel: boolean;
    error: UserReadableError;
}

@Component({
    selector: 'app-confirm-dialog',
    templateUrl: './confirm-dialog.component.html',
    styleUrls: ['./confirm-dialog.component.scss'],
    standalone: false,
})
export class ConfirmDialogComponent implements OnDestroy {
    public isLoading = false;
    public hasChosenPrimary = false;
    public CancelResult = {
        chosePrimary: false,
        choseSecondary: false,
        choseCancel: true,
    } as ConfirmDialogResult;

    private backdropClickSubscription: Subscription;

    public constructor(
        @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogData,
        public dialogRef: MatDialogRef<ConfirmDialogComponent>,
        public customerVerificationService: CustomerVerificationRequestsService,
    ) {
        /* Make sure a result is returned even when the dialog is closed by clicking outside of it */
        this.backdropClickSubscription = dialogRef.backdropClick().subscribe(() => {
            if (!this.data.disableDialogClose) {
                dialogRef.close(this.CancelResult);
            }
        });
    }

    ngOnDestroy() {
        this.backdropClickSubscription?.unsubscribe();
    }

    @HostListener('document:keydown', ['$event'])
    public onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !this.data.hasNote) {
            this.action(this.data.primaryButton);
        } else if (event.key === 'Escape' && !this.data.disableDialogClose) {
            this.dialogRef.close(this.CancelResult);
        }
    }

    public action(dialogButton: ConfirmDialogButton): void {
        let actionResult;

        this.hasChosenPrimary = dialogButton.text === this.data.primaryButton.text;
        this.isLoading = true;
        this.dialogRef.disableClose = true;

        if (!dialogButton.action) {
            // eslint-disable-next-line
            throw new Error("Dialog Button's action has not been defined");
        }

        try {
            actionResult = dialogButton.action();
        } catch (error) {
            this.dialogRef.close({ error });
        }

        if (actionResult instanceof Observable) {
            this.subscribeToActionResult(actionResult);
            return;
        }

        this.dialogRef.close({
            chosePrimary: this.hasChosenPrimary,
            choseSecondary: !this.hasChosenPrimary,
            choseCancel: false,
        } as ConfirmDialogResult);
    }

    public setNote(note: string) {
        this.customerVerificationService.note = note;
    }

    public getTypedChars(string: string): number {
        return string ? string.length : 0;
    }

    private subscribeToActionResult(actionResult: Observable<any>) {
        actionResult.subscribe({
            next: () => {
                this.dialogRef.close({
                    chosePrimary: this.hasChosenPrimary,
                    choseSecondary: !this.hasChosenPrimary,
                    choseCancel: false,
                } as ConfirmDialogResult);
            },
            error: (error: UserReadableError) => {
                this.dialogRef.close({ error });
            },
        });
    }
}
